import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import AdminStrategyEditor from "../../components/admin/AdminStrategyEditor";
import PerformanceChartModal from "../../components/PerformanceChartModal";
import AccountSummaryBar from "../../components/portfolio/AccountSummaryBar";
import { AdminDashboardWrapper } from "../../components/portfolio";
import { useAccountSummary, usePositions } from "../../hooks/useAccountData";
import { useAdminManagedStrategies } from "../../hooks/useAdminManagedStrategies";
import { useStrategyData } from "../../hooks/useStrategyData";
import { useStrategyHoldings } from "../../hooks/useStrategyHoldings";
import { productSearchService } from "../../services/productSearchService";
import type { AdminProductSearchItem } from "../../types/productV3";
import { rollUpInvestorStrategyProducts } from "../../utils/managedStrategyRollup";
import { clipFactorsFromLaunch, resolveLaunchClipMs } from "../../components/performanceChartV3Utils";
import { useTheme } from "../../contexts/ThemeContext";
import { safeParseFloat } from "../../utils/numberUtils";
import userConfigurationService, { type ConfigData } from "../../services/UserConfigurationService";
import { getStrategyAllocationLimitFromConfigData } from "../../utils/strategyAllocationLimits";
import { computeStrategyTotals } from "../../utils/strategyTotals";
import { useStrategySinceAddedReturns } from "../../hooks/useStrategySinceAddedReturns";
import { useStrategyMoveContext } from "../../hooks/useStrategyMoveContext";
import {
  BENCHMARK_CONTRACTS,
  DEFAULT_BENCHMARK_SYMBOL,
  getBenchmarkBySymbol,
} from "../../config/benchmarkContracts";
import { StrategyDesktopTable } from "../dashboard/components/StrategyDesktopTable";
import { buildProductLookup } from "../../utils/strategyUtils";
import StrategyPerformanceWidget from "../dashboard/components/StrategyPerformanceWidget";
import StrategyMoveContextCard from "../dashboard/components/StrategyMoveContextCard";
import StrategyMoveContextModal, { formatDayLabel } from "../dashboard/components/StrategyMoveContextModal";
import CollapsibleDonutCard from "../dashboard/components/StrategyDonutCard";
import StrategyHoldingsSection, { buildProductColorMap } from "../dashboard/components/StrategyHoldingsSection";
import HistorySection from "../dashboard/components/StrategyHistorySection";
import { useActualPerformance, actualItemsToFactors, computeActualPerfMetrics } from "../../hooks/useActualPerformance";

const AdminUserStrategyPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();

  // Viewed user's config (fetched in the effect below) drives the launch-date
  // anchor for this admin view: the customer's computed/actual performance is
  // shown from their launch_date when set and not in the future.
  const [userConfigData, setUserConfigData] = useState<ConfigData | null>(null);
  const launchDate =
    typeof userConfigData?.launch_date === "string" ? userConfigData.launch_date : null;
  const launchClipMs = useMemo(() => resolveLaunchClipMs(launchDate), [launchDate]);

  const {
    strategies,
    reservedEntries,
    productLookup,
    productItems,
    allHistoryItems,
    strategyIdProductMap,
    strategyFactors,
    isLoadingStrategies,
    isLoadingProducts,
    isLoadingFactors,
    isLoading,
    strategyError,
    historyError,
    factorsError,
    totalWeight,
    positionCountsMap,
    strategyInceptionDate,
    strategyPerformanceStartDate,
    strategyPerfMetrics,
    refetchStrategies,
    basketLookup,
    refetchCurrentTab,
    isLoadingHistory,
  } = useStrategyData(userId, { launchDate });

  const { isDarkMode } = useTheme();
  const {
    data: accountSummary,
    error: accountError,
    isLoading: isLoadingAccountSummary,
  } = useAccountSummary(userId);
  const { data: positions } = usePositions(userId);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const {
    data: actualPerfData,
    isLoading: actualPerfLoading,
    error: actualPerfError,
  } = useActualPerformance(userId, true);
  const actualFactors = useMemo(
    () =>
      clipFactorsFromLaunch(
        actualPerfData?.items ? actualItemsToFactors(actualPerfData.items) : [],
        launchClipMs
      ),
    [actualPerfData, launchClipMs]
  );
  const actualMetrics = useMemo(
    () => computeActualPerfMetrics(actualFactors),
    [actualFactors]
  );
  const actualPerfProps = useMemo(
    () => ({
      factors: actualFactors,
      metrics: actualMetrics,
      isLoading: actualPerfLoading,
      error: actualPerfError?.message ?? null,
    }),
    [actualFactors, actualMetrics, actualPerfLoading, actualPerfError]
  );

  const [showPerfModal, setShowPerfModal] = useState(false);
  const [perfModalView, setPerfModalView] = useState<"strategy" | "actual">("strategy");
  const [isEditing, setIsEditing] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(false);
  const [configReloadKey, setConfigReloadKey] = useState(0);
  const rawNlv = accountSummary?.netLiquidationSecurity;
  const selectedInvestorNlv = rawNlv != null ? safeParseFloat(rawNlv) : null;
  const allocationLimit = useMemo(
    () => getStrategyAllocationLimitFromConfigData(userConfigData),
    [userConfigData]
  );
  // A managed strategy is a held product like any other, so every surface below
  // renders the full record set. `strategies` is the self-directed subset;
  // reservedEntries carries the MANAGED_STRATEGY rows it filters out.
  const records = useMemo(
    () => [...strategies, ...reservedEntries],
    [strategies, reservedEntries]
  );

  const strategyTotals = useMemo(
    () => computeStrategyTotals(records, allocationLimit, accountSummary?.netLiquidationSecurity ?? null),
    [records, allocationLimit, accountSummary?.netLiquidationSecurity]
  );
  const { sinceAddedByProduct } = useStrategySinceAddedReturns({
    enabled: true,
    strategies,
    allHistoryItems,
    strategyIdProductMap,
    productLookup,
    isLoadingStrategies,
    isLoadingHistory,
    launchDate,
  });

  const [benchmarkConId, setBenchmarkConId] = useState<number>(
    getBenchmarkBySymbol(DEFAULT_BENCHMARK_SYMBOL)?.conId ?? BENCHMARK_CONTRACTS[0].conId
  );
  const [moveContextDate, setMoveContextDate] = useState<string | null>(null); // null = today
  const [moveContextModalOpen, setMoveContextModalOpen] = useState(false);
  const {
    moveContext,
    isLoading: isLoadingMoveContext,
    error: moveContextError,
    dateBounds: moveContextDateBounds,
  } = useStrategyMoveContext({
    enabled: true,
    strategies,
    productLookup,
    benchmarkConId,
    isLoadingStrategies,
    isLoadingProducts,
    selectedDate: moveContextDate,
  });

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    // The allocation limit (incl. the retirement 95% cap) is derived from this
    // target-user config. Until it resolves we must not let an admin edit/save
    // against the default 100% limit, so track loading/error explicitly.
    setConfigLoading(true);
    setConfigError(false);
    setUserConfigData(null);
    userConfigurationService
      .getUserConfiguration(userId)
      .then((config) => {
        if (!cancelled) {
          setUserConfigData(config.data);
          setConfigLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setConfigError(true);
          setConfigLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [userId, configReloadKey]);

  const managedIds = useMemo(
    () =>
      records
        .filter((record) => record.entityType === "MANAGED_STRATEGY")
        .map((record) => record.productId),
    [records]
  );
  const [managedReloadToken, setManagedReloadToken] = useState(0);
  const managed = useAdminManagedStrategies(managedIds, true, managedReloadToken);

  // Managed strategies the add-search can offer. Fetched separately because
  // searchAdminProducts (which feeds productItems) strips MANAGED_STRATEGY rows
  // by design; the all-types admin route is the only one that returns them.
  const [managedCatalogue, setManagedCatalogue] = useState<AdminProductSearchItem[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    productSearchService
      .searchAdminProductsAllTypes(
        { productType: "MANAGED_STRATEGY", page: 1, pageSize: 2000 },
        controller.signal
      )
      .then((response) => {
        if (controller.signal.aborted) return;
        setManagedCatalogue(response.items);
      })
      // Non-critical: without it the editor still edits and removes the managed
      // rows the account holds, it just cannot offer a new one.
      .catch(() => {});
    return () => controller.abort();
  }, []);

  // Titles for managed rows resolve through the same lookup as every other
  // strategy row. Held strategies come from the resolved-by-id map so a PRIVATE
  // one still names itself even when it is absent from the catalogue page.
  const recordLookup = useMemo(() => {
    const managedItems = [
      ...managedCatalogue,
      ...Array.from(managed.managedById.values()),
    ].map((item) => ({
      productType: "MANAGED_STRATEGY" as const,
      productId: item.productId,
      title: item.title,
      providerId: null,
      providerName: "",
    }));
    if (managedItems.length === 0) return productLookup;
    const merged = new Map(productLookup);
    for (const [key, entry] of buildProductLookup(managedItems)) {
      merged.set(key, entry);
    }
    return merged;
  }, [productLookup, managedCatalogue, managed.managedById]);

  // Flatten to portfolio/basket level so a managed strategy's leaves reach the
  // holdings math. Direct holdings pass through unchanged.
  const rollup = useMemo(
    () =>
      rollUpInvestorStrategyProducts(
        records,
        managed.managedById,
        productItems,
        basketLookup
      ),
    [records, managed.managedById, productItems, basketLookup]
  );

  // A short rolled-up view on a live book reads as "the user holds less than
  // they do", so an incomplete expansion must always be visible.
  const rollupWarning = useMemo(() => {
    if (managed.isLoading) return null;
    // Count distinct strategies, not issue rows: a failed fetch also surfaces
    // as MANAGED_NOT_FOUND from the rollup, and must not be counted twice.
    const unresolvedManaged = managedIds.filter((id) => !managed.managedById.has(id)).length;
    if (unresolvedManaged > 0) {
      return `Rolled-up view is incomplete: ${unresolvedManaged} managed strateg${
        unresolvedManaged === 1 ? "y" : "ies"
      } could not be loaded.`;
    }
    if (rollup.issues.some((issue) => issue.code === "MANAGED_COMPOSITION_INVALID")) {
      return "Rolled-up view is incomplete: a managed strategy composition is unavailable.";
    }
    if (rollup.issues.some((issue) => issue.code === "PRODUCT_NOT_FOUND")) {
      return "Rolled-up view is incomplete: a product could not be resolved from the catalogue.";
    }
    return null;
  }, [managed.isLoading, managed.managedById, managedIds, rollup.issues]);

  const holdingsInput = useMemo(
    () =>
      rollup.rows.map((row) => ({
        entityType: row.entityType,
        productId: row.productId,
        weight: row.allocatedWeight,
        driftedWeight: row.currentWeight,
        displayName: row.displayName,
      })),
    [rollup.rows]
  );
  const holdings = useStrategyHoldings(holdingsInput, productItems, basketLookup);
  const productColorMap = useMemo(() => buildProductColorMap(records), [records]);

  // Position counts for a managed row are the summed counts of its children.
  const recordPositionCountsMap = useMemo(() => {
    if (managed.managedById.size === 0 && managedIds.length === 0) return positionCountsMap;
    const merged = new Map(positionCountsMap);
    for (const [managedId, counts] of rollup.managedCounts) {
      if (counts) merged.set(`MANAGED_STRATEGY-${managedId}`, counts);
    }
    return merged;
  }, [positionCountsMap, rollup.managedCounts, managed.managedById, managedIds]);

  const recordsTotalWeight = useMemo(
    () => records.reduce((sum, record) => sum + parseFloat(record.weight), 0),
    [records]
  );

  if (!userId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">User ID is required</p>
      </div>
    );
  }

  return (
    <AdminDashboardWrapper userId={userId}>
      <div className="flex flex-col h-full min-h-screen bg-gray-50 dark:bg-black transition-colors duration-300">
        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          {strategyError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <div className="text-red-700 dark:text-red-400">{strategyError}</div>
              <button
                onClick={refetchStrategies}
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm mt-2 underline"
              >
                Retry
              </button>
            </div>
          )}

          {isLoading && !strategyError && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="bg-white dark:bg-rp-surface rounded-lg p-6 border border-gray-200 dark:border-rp-separator animate-pulse"
                  >
                    <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-1/3 mb-3" />
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-1/4" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isLoading && !strategyError && (
            <>
              <div className="mb-4 -mx-1 sm:-mx-3 -mt-1 sm:-mt-3">
                <AccountSummaryBar
                  accountSummary={accountSummary ?? null}
                  positionCount={positions?.length ?? 0}
                  isExpanded={summaryExpanded}
                  onToggleExpand={() => setSummaryExpanded((p) => !p)}
                  isDarkMode={isDarkMode}
                  accountError={accountError?.message}
                  isCustomer={false}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="flex flex-col gap-6">
                  <StrategyPerformanceWidget
                    metrics={strategyPerfMetrics}
                    factors={strategyFactors}
                    isLoading={isLoadingFactors}
                    error={factorsError}
                    onClick={() => { setPerfModalView("actual"); setShowPerfModal(true); }}
                    actualPerf={actualPerfProps}
                  />
                  <StrategyMoveContextCard
                    moveContext={moveContext}
                    isLoading={isLoadingMoveContext}
                    error={moveContextError}
                    benchmarks={BENCHMARK_CONTRACTS}
                    selectedConId={benchmarkConId}
                    onSelectBenchmark={setBenchmarkConId}
                    selectedDate={moveContextDate}
                    onSelectDate={setMoveContextDate}
                    dateBounds={moveContextDateBounds}
                    onExpand={() => setMoveContextModalOpen(true)}
                  />
                  <CollapsibleDonutCard
                    strategies={records}
                    productLookup={recordLookup}
                    totalWeight={recordsTotalWeight}
                    capacityPct={allocationLimit}
                  />
                </div>

                <div className="lg:col-span-2">
                  {isEditing ? (
                    <AdminStrategyEditor
                      key={userId}
                      userId={userId}
                      records={records}
                      productItems={productItems}
                      managedItems={managedCatalogue}
                      productLookup={recordLookup}
                      nlv={selectedInvestorNlv}
                      allocationLimit={allocationLimit}
                      isLoadingNlv={isLoadingAccountSummary}
                      onSave={() => {
                        setIsEditing(false);
                        setManagedReloadToken((token) => token + 1);
                        refetchCurrentTab();
                      }}
                      onCancel={() => setIsEditing(false)}
                    />
                  ) : (
                    <div className="bg-white dark:bg-rp-surface rounded-lg border border-gray-200 dark:border-rp-separator overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-rp-separator">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Strategy Composition</h3>
                        <div className="flex items-center gap-2">
                          {configError && (
                            <span className="text-xs text-red-600 dark:text-red-400">
                              Account limits unavailable.{" "}
                              <button
                                onClick={() => setConfigReloadKey((k) => k + 1)}
                                className="underline hover:text-red-800 dark:hover:text-red-300"
                              >
                                Retry
                              </button>
                            </span>
                          )}
                          <button
                            onClick={() => setIsEditing(true)}
                            disabled={configLoading || configError}
                            title={
                              configLoading
                                ? "Loading account allocation limits"
                                : configError
                                  ? "Account allocation limits unavailable"
                                  : undefined
                            }
                            className="px-3 py-1.5 text-xs font-medium text-rp-amber border border-rp-amber rounded hover:bg-rp-amber/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Edit Strategy
                          </button>
                        </div>
                      </div>
                      {records.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                          No strategy configured for this investor
                        </div>
                      ) : (
                        <StrategyDesktopTable
                          strategies={records}
                          productLookup={recordLookup}
                          positionCountsMap={recordPositionCountsMap}
                          sinceAddedByProduct={sinceAddedByProduct}
                          accountSummary={accountSummary ?? null}
                          totals={strategyTotals}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {records.length > 0 && (
                <div className="mb-8">
                  {rollupWarning && (
                    <div className="mb-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-800 dark:text-amber-300">
                      {rollupWarning}
                    </div>
                  )}
                  <StrategyHoldingsSection
                    holdings={holdings.holdings}
                    summary={holdings.summary}
                    missingProducts={holdings.missingProducts}
                    isLoading={holdings.isLoading || isLoading || managed.isLoading}
                    nlv={selectedInvestorNlv}
                    title="Underlying Holdings (rolled-up)"
                    productColorMap={productColorMap}
                  />
                </div>
              )}

              <HistorySection
                items={allHistoryItems}
                isLoading={isLoadingHistory}
                error={historyError}
                productLookup={productLookup}
                strategyIdProductMap={strategyIdProductMap}
              />
            </>
          )}
        </main>

        <PerformanceChartModal
          isOpen={showPerfModal}
          onClose={() => setShowPerfModal(false)}
          entityId={0}
          entityType="STRATEGY"
          entityName="Strategy"
          dataSource="v3-factors"
          entityCreatedAt={strategyPerformanceStartDate ?? strategyInceptionDate}
          adminUserId={userId}
          strategyComparison={{ strategyFactors, products: [] }}
          showActualToggle
          initialPerfView={perfModalView}
          actualFactors={actualFactors}
          launchDate={launchDate}
        />

        {moveContextModalOpen && moveContext && (
          <StrategyMoveContextModal
            moveContext={moveContext}
            dayLabel={
              !moveContextDate || moveContextDate === moveContextDateBounds?.max
                ? "today"
                : formatDayLabel(moveContextDate)
            }
            onClose={() => setMoveContextModalOpen(false)}
          />
        )}
      </div>
    </AdminDashboardWrapper>
  );
};

export default AdminUserStrategyPage;
